import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  MessageCircle, 
  Star, 
  Shield, 
  Clock, 
  Car, 
  Sparkles, 
  Heart, 
  Zap, 
  Award,
  CheckCircle,
  ArrowRight,
  MapPin
} from 'lucide-react';

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="./images/hero_spa_interior_1.jpeg" 
            alt="Serene spa interior with candles and soft lighting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/30"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="space-y-4">
              <h1 className="text-hero font-bold leading-tight">
                Your Glow, <span className="text-gradient bg-gradient-to-r from-pink-300 to-pink-100 bg-clip-text text-transparent">Your Power</span>
              </h1>
              <p className="text-xl md:text-2xl font-light text-pink-100">
                Redefining beauty, health, and wellness in Polokwane
              </p>
            </div>
            
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Experience premium body contouring, rejuvenating treatments, and personalized wellness solutions at Polokwane's most trusted beauty and health studio.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 animate-glow text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Book Your Glow Session
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Now: 069 552 3598
                </Button>
              </a>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 pt-8 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Professional & Safe</span>
              </div>
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4" />
                <span>Secured Parking</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                <span>Premium Treatments</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-section text-foreground">Who We Are</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  At Glow by Precious, we believe that true beauty radiates from within. Located in the heart of Polokwane, we are your premier destination for advanced beauty, health, and wellness treatments.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Our Mission</h3>
                <p className="text-muted-foreground leading-relaxed">
                  To empower every client to discover their inner glow through personalized, professional treatments that enhance both physical appearance and overall well-being. We combine cutting-edge technology with compassionate care to deliver transformative results.
                </p>
              </div>
              
              <div className="flex items-center gap-4 pt-4">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                </div>
                <span className="text-sm text-muted-foreground">Trusted by hundreds of satisfied clients</span>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="./images/diverse_women_wellness_1.jpeg" 
                alt="Diverse women celebrating wellness and beauty"
                className="w-full h-96 object-cover rounded-2xl shadow-elegant"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 gradient-primary rounded-full flex items-center justify-center shadow-elegant animate-float">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Why Choose Glow by Precious?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the difference that professional care, advanced technology, and personalized attention can make in your wellness journey.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Professional & Safe</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our treatments are performed by qualified professionals using the highest safety standards and hygiene protocols.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Advanced Technology</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We use cutting-edge equipment including RF, cavitation, laser, and vacuum bipolar technology for optimal results.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Personalized Care</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Every treatment is tailored to your unique needs and goals, ensuring the best possible outcomes for your wellness journey.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Car className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Convenient Location</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Located in central Polokwane with secured parking available for your peace of mind and convenience.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Flexible Hours</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Open Monday to Friday 9AM-5PM, Saturday 9AM-1PM to accommodate your busy schedule and lifestyle needs.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Proven Results</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Join hundreds of satisfied clients who have achieved their wellness goals through our comprehensive treatment programs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Our Premium Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover our comprehensive range of beauty, health, and wellness treatments designed to help you look and feel your absolute best.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <div className="relative overflow-hidden rounded-t-lg">
                <img 
                  src="./images/body_contouring_treatment_2.jpeg" 
                  alt="Body contouring treatment in modern wellness center"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Body Contouring</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Advanced cavitation, RF, laser, and vacuum bipolar RF treatments to sculpt and tone your body naturally.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Non-invasive • Effective</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <div className="relative overflow-hidden rounded-t-lg">
                <img 
                  src="./images/iv_drip_therapy_1.jpeg" 
                  alt="IV drip therapy wellness treatment"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">IV Drip Therapy</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Customized IV drips for weight loss, energy boost, skin glow, anti-aging, and overall wellness enhancement.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Personalized • Fast Results</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <div className="relative overflow-hidden rounded-t-lg">
                <img 
                  src="./images/wellness_massage_therapy_1.jpeg" 
                  alt="Professional massage therapy treatment"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Therapeutic Massage</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Relax, detox, and rejuvenate with our professional massage treatments designed for total body wellness.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Relaxing • Therapeutic</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Slimming & Glow Injections</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Target stubborn fat and boost skin radiance with our specialized injection treatments for enhanced beauty.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Targeted • Radiant Results</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Medical Weight Loss</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Comprehensive weight management programs tailored to your individual health needs and lifestyle goals.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Medical • Sustainable</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 group cursor-pointer">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Butt Vacuum</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Specialized vacuum therapy treatment designed to enhance and lift for natural-looking body contouring results.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">Enhancing • Natural</span>
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                View All Services
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Your Safety & Comfort is Our Priority</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We maintain the highest standards of professionalism, safety, and hygiene to ensure your complete peace of mind.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Safety First</h3>
              <p className="text-sm text-muted-foreground">Strict hygiene protocols and safety standards</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Professional Team</h3>
              <p className="text-sm text-muted-foreground">Qualified and experienced practitioners</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <Car className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Secured Parking</h3>
              <p className="text-sm text-muted-foreground">Safe and convenient parking available</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Quality Assured</h3>
              <p className="text-sm text-muted-foreground">Premium equipment and proven results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Booking Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Begin Your Transformation?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Take the first step towards your wellness goals. Book your consultation today and discover what Glow by Precious can do for you.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp Us Now
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-12">
              <div className="text-center space-y-2">
                <MapPin className="h-6 w-6 mx-auto text-pink-200" />
                <h3 className="font-semibold text-white">Visit Us</h3>
                <p className="text-sm text-pink-100">100 Market Street, Office No. 12<br />Amie Bava Square, Polokwane</p>
              </div>
              
              <div className="text-center space-y-2">
                <Clock className="h-6 w-6 mx-auto text-pink-200" />
                <h3 className="font-semibold text-white">Opening Hours</h3>
                <p className="text-sm text-pink-100">Mon-Fri: 9:00 AM - 5:00 PM<br />Sat: 9:00 AM - 1:00 PM<br />Sun & Holidays: Closed</p>
              </div>
              
              <div className="text-center space-y-2">
                <Phone className="h-6 w-6 mx-auto text-pink-200" />
                <h3 className="font-semibold text-white">Contact</h3>
                <p className="text-sm text-pink-100">Phone: 069 552 3598<br />WhatsApp Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
